import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

/**
 * Page de réinitialisation de mot de passe
 * Accessible via le lien envoyé par email avec un token
 */
const ResetPassword = ({ token, onBackToLogin, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Vérifier le token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_ENDPOINTS.AUTH}/verify-reset-token/${token}`);
        const data = await response.json();

        if (data.success) {
          setTokenValid(true);
          setUserEmail(data.data.email);
        } else {
          setError(data.error || 'Token invalide ou expiré');
        }
      } catch (err) {
        setError('Erreur de connexion au serveur');
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setError('Token manquant');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        if (onSuccess) {
          setTimeout(() => onSuccess(), 3000);
        }
      } else {
        setError(data.error || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '40px',
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '30px'
    },
    logoImg: {
      height: '50px'
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '10px',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '30px',
      textAlign: 'center'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333'
    },
    input: {
      padding: '12px 16px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'border-color 0.2s',
      outline: 'none'
    },
    button: {
      padding: '14px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed'
    },
    error: {
      background: '#fee',
      color: '#c00',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '14px',
      textAlign: 'center'
    },
    success: {
      background: '#e8f5e9',
      color: '#2e7d32',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center'
    },
    successIcon: {
      fontSize: '48px',
      marginBottom: '15px'
    },
    successTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '10px'
    },
    successText: {
      fontSize: '14px',
      lineHeight: '1.5'
    },
    backLink: {
      textAlign: 'center',
      marginTop: '20px'
    },
    link: {
      color: '#667eea',
      textDecoration: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666'
    },
    loadingIcon: {
      fontSize: '32px',
      marginBottom: '15px'
    },
    hint: {
      fontSize: '12px',
      color: '#999',
      marginTop: '4px'
    },
    emailInfo: {
      background: '#f5f5f5',
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '20px',
      textAlign: 'center',
      fontSize: '14px',
      color: '#666'
    }
  };

  // Affichage pendant la vérification
  if (verifying) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={styles.loading}>
            <div style={styles.loadingIcon}>⏳</div>
            <div>Vérification du lien...</div>
          </div>
        </div>
      </div>
    );
  }

  // Token invalide
  if (!tokenValid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={{ ...styles.error, padding: '30px' }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>❌</div>
            <div style={{ fontWeight: '600', marginBottom: '10px' }}>Lien invalide ou expiré</div>
            <div style={{ fontWeight: 'normal' }}>
              Ce lien de réinitialisation n'est plus valide. Les liens expirent après 1 heure.
            </div>
          </div>
          <div style={styles.backLink}>
            <span style={styles.link} onClick={onBackToLogin}>
              ← Retour à la connexion
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Succès
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
          </div>
          <div style={styles.success}>
            <div style={styles.successIcon}>✅</div>
            <div style={styles.successTitle}>Mot de passe modifié !</div>
            <div style={styles.successText}>
              Votre mot de passe a été réinitialisé avec succès.
              <br />
              Vous allez être redirigé vers la page de connexion...
            </div>
          </div>
          <div style={styles.backLink}>
            <span style={styles.link} onClick={onBackToLogin}>
              Se connecter maintenant
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de réinitialisation
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src="/logo_connect.png" alt="R-Connect" style={styles.logoImg} />
        </div>

        <h1 style={styles.title}>Nouveau mot de passe</h1>
        <p style={styles.subtitle}>
          Choisissez un nouveau mot de passe pour votre compte
        </p>

        <div style={styles.emailInfo}>
          Compte : <strong>{userEmail}</strong>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Nouveau mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              style={styles.input}
            />
            <div style={styles.hint}>Minimum 6 caractères</div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirmer le mot de passe</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={styles.input}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !password || !confirmPassword}
            style={{
              ...styles.button,
              ...(loading || !password || !confirmPassword ? styles.buttonDisabled : {})
            }}
          >
            {loading ? 'Modification en cours...' : 'Modifier le mot de passe'}
          </button>
        </form>

        <div style={styles.backLink}>
          <span style={styles.link} onClick={onBackToLogin}>
            ← Retour à la connexion
          </span>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
